import "./IssueCard.css";
import { Chip, STATUS, StatusPill } from "./Status";
import type { StatusKey } from "./Status";
import type { Issue } from "../lib/tasks";
import { isHeld, isMerging, isWorking } from "../lib/tasks";
import { tx } from "../i18n";

/** 보드 카드. 표식은 **넷 다 색이 유일한 신호가 아니다**:
 *  - 나루 할당 → 왼쪽 3px 테두리 + 「◆ 나루 할당」 칩(사람 확정 2026-09-12)
 *  - 병합 대기 → 초록 테두리 + 「⧉ 병합 대기」 칩. 별도 열을 만들지 않는다
 *  - 에픽 → 「◇」 + 「n / m 완료」 분수. 박스로 묶지 않는다
 *  - 도는 중 → 점이 깜빡이고 **글자로도** 「도는 중」이라 적힌다
 *
 *  에픽 하위 카드에는 우측에 필터 보기(⌕)가 붙는다 — 누르면 보드가 그 에픽의 하위만
 *  남기고 좁혀진다. **화면 전환이 아니라 필터다.** */
export function IssueCard({
  t,
  epicTitle,
  milestoneLabel,
  now,
  onOpen,
  onFilterEpic,
  dragging,
  colStatus,
  selected,
  onSelect,
  ...drag
}: {
  t: Issue;
  epicTitle?: string;
  /** 마일스톤 칩의 글자. 호출부가 골라 준다 — 카드는 마일스톤 목록을 모른다. */
  milestoneLabel?: string;
  /** 「도는 중」 경과 시간의 기준점. 생략하면 실제 지금이다 — 패턴 페이지의 고정
   *  목업 시각(`MOCK_NOW`)을 줄 때만 넘긴다. */
  now?: number;
  onOpen: () => void;
  onFilterEpic?: () => void;
  dragging?: boolean;
  /** 이 카드가 선 열의 대표 상태. 카드 상태가 그것과 다르면(막힘 · 취소) 상태 칩을
   *  붙인다 — **열은 어디쯤 왔나를, 칩은 정확히 무엇인지를** 말한다. 열이 다섯뿐이라
   *  이 칩이 없으면 막힘이 대기와, 취소가 완료와 구분되지 않는다. */
  colStatus?: StatusKey;
  /** 보드 선택 줄(Figma 31:2 ③). 주면 왼쪽 위에 체크가 선다 — 카드를 누르면 여전히 상세가 열린다. */
  selected?: boolean;
  onSelect?: (on: boolean) => void;
  draggable?: boolean;
  onDragStart?: React.DragEventHandler;
  onDragEnd?: React.DragEventHandler;
  onDragOver?: React.DragEventHandler;
  onDrop?: React.DragEventHandler;
}) {
  const held = isHeld(t);
  const merging = isMerging(t);
  const working = isWorking(t);
  const isEpic = t.childCount > 0;

  const offStatus = colStatus !== undefined && t.status !== colStatus;
  const marks = [
    offStatus && STATUS[t.status].label,
    held && tx("issue.held"),
    merging && tx("issue.merging"),
    working && tx("issue.working"),
  ].filter(Boolean);

  return (
    <article
      {...drag}
      className={[
        "icard",
        held && "icard--held",
        merging && "icard--merging",
        t.status === "cancelled" && "icard--cancelled",
        dragging && "is-dragging",
        selected && "is-picked",
      ]
        .filter(Boolean)
        .join(" ")}
      /* 카드 전체가 누르는 자리다. 표식은 글자로도 있으니 aria-label 이 그것을 그대로 읽는다. */
      tabIndex={0}
      role="button"
      aria-label={`${t.title}${marks.length ? ` · ${marks.join(" · ")}` : ""}`}
      onClick={onOpen}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          onOpen();
        }
      }}
    >
      <div className="icard__top">
        {onSelect ? (
          /* 체크를 누른 것이 카드 클릭(상세 열기) · 끌기로 번지지 않게 막는다. */
          <label className="b-check icard__pick" onClick={(e) => e.stopPropagation()} onKeyDown={(e) => e.stopPropagation()}>
            <input
              type="checkbox"
              checked={!!selected}
              aria-label={tx("boardSel.pick", { title: t.title })}
              onChange={(e) => onSelect(e.target.checked)}
            />
          </label>
        ) : null}
        <span className="icard__grip" aria-hidden="true">
          ⠿
        </span>
        {isEpic ? (
          <span className="icard__epic" aria-hidden="true">
            ◇
          </span>
        ) : null}
        <span className="icard__title">{t.title}</span>
        {t.parentId && onFilterEpic ? (
          <button
            type="button"
            className="b-ibtn b-ibtn-sm b-btn-ghost icard__filter"
            title={tx("issue.epicOnly", { title: epicTitle ?? tx("issue.thisEpic") })}
            aria-label={tx("issue.epicOnly", { title: epicTitle ?? tx("issue.thisEpic") })}
            /* 카드 클릭은 상세를 여는 것이라, 여기서 막지 않으면 필터와 상세가 같이 뜬다. */
            onClick={(e) => {
              e.stopPropagation();
              onFilterEpic();
            }}
          >
            ⌕
          </button>
        ) : null}
      </div>

      <div className="icard__chips">
        {offStatus ? <StatusPill status={t.status} /> : null}
        {held ? <Chip tone="accent">◆ {tx("issue.held")}</Chip> : null}
        {merging ? <Chip tone="success">⧉ {tx("issue.merging")}</Chip> : null}
        {working ? (
          <Chip tone="info">
            <span className="icard__dot" aria-hidden="true" /> {tx("issue.working")} · {minutesSince(t.openSession!.startedAt, now)}
          </Chip>
        ) : null}
        {isEpic ? (
          <Chip tone="warning">
            {tx("issue.doneOf", { done: t.doneChildCount, total: t.childCount })}
          </Chip>
        ) : null}
        {t.type ? <Chip>◇ {t.type}</Chip> : <Chip>{tx("issue.noType")}</Chip>}
        {t.milestoneId ? <Chip>{milestoneLabel ?? t.milestoneId}</Chip> : null}
        {t.priority !== "normal" ? (
          <Chip tone={t.priority === "critical" ? "error" : "warning"}>
            {priorityText(t.priority)}
          </Chip>
        ) : null}
      </div>
    </article>
  );
}

/** `now` 를 받는 것은 목업 검토용 고정 시각(패턴 페이지)과 실제 지금을 같은 함수로
 *  재는 자리다 — 기본은 실제 지금이다. */
export function minutesSince(iso: string, now = Date.now()): string {
  const e = elapsed(iso, now);
  return e.unit === "now" ? tx("issue.since.now") : tx(`issue.since.${e.unit}`, { n: e.n });
}

/** 「3시간」처럼 「째」 없는 경과 — 시점을 말하는 자리(「3시간 전」의 앞쪽)가 쓴다. 1분이 안 되면 「방금」. */
export function spanSince(iso: string, now = Date.now()): string {
  const e = elapsed(iso, now);
  return e.unit === "now" ? tx("issue.since.now") : tx(`issue.span.${e.unit}`, { n: e.n });
}

function elapsed(iso: string, now: number): { unit: "now" | "min" | "hour" | "day"; n: number } {
  const min = Math.round((now - Date.parse(iso)) / 60_000);
  if (min < 1) return { unit: "now", n: 0 };
  if (min < 60) return { unit: "min", n: min };
  const h = Math.floor(min / 60);
  return h < 24 ? { unit: "hour", n: h } : { unit: "day", n: Math.floor(h / 24) };
}

/** 등급 · 복잡도 낱말 — 카드 · 표 · 상세가 같은 표를 읽는다. */
export function priorityText(p: "critical" | "high" | "normal"): string {
  return tx(`issue.priority.${p}`);
}
export function complexityText(c: "deep" | "hard" | "normal" | "light"): string {
  return tx(`issue.complexity.${c}`);
}
